<template>
  <q-dialog :model-value="modelValue" persistent>
    <q-card style="width: 800px; max-width: 95vw">
      <q-toolbar>
        <q-toolbar-title class="text-blue-brand text-bold">
          Confirm Reverse Direction
        </q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>

      <q-card-section>
        <div class="text-body2 q-mb-md">
          This will swap the launch and receive locations. The pig will run in
          the opposite direction along the pipeline section.
        </div>

        <div v-if="loading" class="row justify-center q-pa-lg">
          <q-spinner color="blue-brand" size="48px" />
        </div>

        <div v-else-if="errorMessage" class="text-negative text-center q-pa-md">
          {{ errorMessage }}
        </div>

        <div v-else class="row q-col-gutter-md">
          <div class="col-6">
            <div class="text-subtitle2 text-center q-mb-sm text-weight-bold">
              Current Direction
            </div>
            <div
              ref="forwardMapEl"
              style="
                height: 260px;
                width: 100%;
                border: 1px solid #ccc;
                border-radius: 4px;
              "
            />
          </div>
          <div class="col-6">
            <div class="text-subtitle2 text-center q-mb-sm text-weight-bold">
              Reversed Direction
            </div>
            <div
              ref="reversedMapEl"
              style="
                height: 260px;
                width: 100%;
                border: 1px solid #ccc;
                border-radius: 4px;
              "
            />
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="center">
        <q-btn no-caps class="btn-brand" label="Cancel" @click="onCancel" />
        <q-btn
          no-caps
          class="btn-brand"
          icon="fas fa-check"
          label="Confirm"
          :disable="loading || !!errorMessage"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import L from 'leaflet';
import PigRunService from '@/services/PigRunService';
import PigRunCommon from '@/utils/PigRunCommon';
import { escapeHtml } from '@/utils/HtmlEscape';

interface MarkerData {
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  sortOrder: number;
}

export default defineComponent({
  name: 'BackwardsConfirmDialog',

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    pigRunId: {
      type: Number as PropType<number>,
      required: true,
    },
    markers: {
      type: Array as PropType<MarkerData[]>,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'confirmed', 'cancelled'],

  data() {
    return {
      loading: false,
      errorMessage: '',
      forwardMarkers: [] as MarkerData[],
      reversedMarkers: [] as MarkerData[],
      forwardMap: null as L.Map | null,
      reversedMap: null as L.Map | null,
      forwardLayer: null as L.LayerGroup | null,
      reversedLayer: null as L.LayerGroup | null,
      forwardPigMarker: null as L.Marker | null,
      reversedPigMarker: null as L.Marker | null,
      animationTimer: null as ReturnType<typeof setInterval> | null,
      animationProgress: 0,
    };
  },

  watch: {
    modelValue(val: boolean) {
      if (val) {
        this.onOpen();
      } else {
        this.cleanup();
      }
    },
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    async onOpen() {
      this.loading = true;
      this.errorMessage = '';
      this.forwardMarkers = [];
      this.reversedMarkers = [];

      try {
        let markers: MarkerData[] = [];

        if (this.markers && this.markers.length > 0) {
          markers = this.markers;
        } else {
          const response = await PigRunService.getPigRun(this.pigRunId);
          const runData = response.data;
          markers = runData.markers || [];

          if ((!markers || markers.length === 0) && runData.pipelineSectionId) {
            const psResponse = await PigRunService.getPipelineSectionMarkers(
              runData.pipelineSectionId
            );
            markers = psResponse.data.markers || [];
          }
        }

        if (!markers || markers.length === 0) {
          this.errorMessage =
            'No markers found for this pig run. Cannot preview direction.';
          this.loading = false;
          return;
        }

        this.forwardMarkers = [...markers].sort(
          (a, b) => a.sortOrder - b.sortOrder
        );

        this.reversedMarkers = this.buildReversedMarkers(this.forwardMarkers);
        this.loading = false;

        await this.$nextTick();
        this.initMaps();
        this.plotMarkers();
        this.startAnimation();
      } catch (error) {
        console.error('Failed to load pig run markers:', error);
        this.errorMessage = 'Failed to load pipeline section data.';
        this.loading = false;
      }
    },

    buildReversedMarkers(forward: MarkerData[]): MarkerData[] {
      const reversed = [...forward].reverse();
      return reversed.map((m) => {
        const copy = { ...m };
        if (copy.description === 'LAUNCH') {
          copy.description = 'RECEIVE';
        } else if (copy.description === 'RECEIVE') {
          copy.description = 'LAUNCH';
        }
        return copy;
      });
    },

    initMaps() {
      this.destroyMaps();

      const forwardEl = this.$refs.forwardMapEl as HTMLElement;
      const reversedEl = this.$refs.reversedMapEl as HTMLElement;
      if (!forwardEl || !reversedEl) return;

      this.forwardMap = L.map(forwardEl, {
        zoomControl: true,
        attributionControl: false,
      }).setView([0, 0], 2);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.forwardMap as L.Map);

      this.forwardLayer = L.layerGroup().addTo(this.forwardMap as L.Map);

      this.reversedMap = L.map(reversedEl, {
        zoomControl: true,
        attributionControl: false,
      }).setView([0, 0], 2);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.reversedMap as L.Map);

      this.reversedLayer = L.layerGroup().addTo(this.reversedMap as L.Map);
    },

    plotMarkers() {
      this.plotOnMap(
        this.forwardMap as L.Map | null,
        this.forwardLayer as L.LayerGroup | null,
        this.forwardMarkers,
        'forward'
      );
      this.plotOnMap(
        this.reversedMap as L.Map | null,
        this.reversedLayer as L.LayerGroup | null,
        this.reversedMarkers,
        'reversed'
      );
    },

    plotOnMap(
      map: L.Map | null,
      layer: L.LayerGroup | null,
      markers: MarkerData[],
      which: 'forward' | 'reversed'
    ) {
      if (!map || !layer) return;

      const latLngs: L.LatLng[] = [];

      for (const m of markers) {
        if (!m.latitude || !m.longitude) continue;
        const latlng = L.latLng(m.latitude, m.longitude);
        latLngs.push(latlng);

        const icon = PigRunCommon.getIcon(m.description);
        L.marker(latlng, { icon })
          .bindTooltip(escapeHtml(m.name), {
            permanent: false,
            direction: 'top',
            offset: [0, -8],
          })
          .addTo(layer);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(layer);
      }

      const pigMarker = L.marker(latLngs[0] || L.latLng(0, 0), {
        icon: PigRunCommon.pigIcon,
        zIndexOffset: 1000,
      }).addTo(layer);

      if (which === 'forward') {
        this.forwardPigMarker = pigMarker;
      } else {
        this.reversedPigMarker = pigMarker;
      }

      setTimeout(() => {
        if (!map) return;
        map.invalidateSize();
        if (latLngs.length > 1) {
          map.fitBounds(L.latLngBounds(latLngs), { padding: [20, 20] });
        } else if (latLngs.length === 1) {
          map.setView(latLngs[0], 14);
        }
      }, 150);
    },

    startAnimation() {
      this.stopAnimation();
      this.animationProgress = 0;

      const forwardLatLngs = this.forwardMarkers
        .filter((m) => m.latitude && m.longitude)
        .map((m) => L.latLng(m.latitude, m.longitude));

      const reversedLatLngs = this.reversedMarkers
        .filter((m) => m.latitude && m.longitude)
        .map((m) => L.latLng(m.latitude, m.longitude));

      if (forwardLatLngs.length < 2 || reversedLatLngs.length < 2) return;

      const step = 0.005;
      this.animationTimer = setInterval(() => {
        this.animationProgress += step;
        if (this.animationProgress > 1) {
          this.animationProgress = 0;
        }

        const forwardPos = this.interpolateAlongPath(
          forwardLatLngs,
          this.animationProgress
        );
        const reversedPos = this.interpolateAlongPath(
          reversedLatLngs,
          this.animationProgress
        );

        if (this.forwardPigMarker && forwardPos) {
          this.forwardPigMarker.setLatLng(forwardPos);
        }
        if (this.reversedPigMarker && reversedPos) {
          this.reversedPigMarker.setLatLng(reversedPos);
        }
      }, 50);
    },

    interpolateAlongPath(path: L.LatLng[], progress: number): L.LatLng | null {
      if (path.length < 2) return null;

      const totalDist = this.totalPathDistance(path);
      const targetDist = totalDist * progress;

      let accumulated = 0;
      for (let i = 0; i < path.length - 1; i++) {
        const segDist = path[i].distanceTo(path[i + 1]);
        if (accumulated + segDist >= targetDist) {
          const segProgress =
            segDist > 0 ? (targetDist - accumulated) / segDist : 0;
          const lat =
            path[i].lat + (path[i + 1].lat - path[i].lat) * segProgress;
          const lng =
            path[i].lng + (path[i + 1].lng - path[i].lng) * segProgress;
          return L.latLng(lat, lng);
        }
        accumulated += segDist;
      }
      return path[path.length - 1];
    },

    totalPathDistance(path: L.LatLng[]): number {
      let dist = 0;
      for (let i = 0; i < path.length - 1; i++) {
        dist += path[i].distanceTo(path[i + 1]);
      }
      return dist;
    },

    stopAnimation() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }
    },

    destroyMaps() {
      this.stopAnimation();
      if (this.forwardMap) {
        this.forwardMap.remove();
        this.forwardMap = null;
        this.forwardLayer = null;
        this.forwardPigMarker = null;
      }
      if (this.reversedMap) {
        this.reversedMap.remove();
        this.reversedMap = null;
        this.reversedLayer = null;
        this.reversedPigMarker = null;
      }
    },

    cleanup() {
      this.destroyMaps();
      this.forwardMarkers = [];
      this.reversedMarkers = [];
      this.errorMessage = '';
    },

    onConfirm() {
      this.$emit('update:modelValue', false);
      this.$emit('confirmed');
    },

    onCancel() {
      this.$emit('update:modelValue', false);
      this.$emit('cancelled');
    },
  },
});
</script>
